import React, { useState, useEffect, useRef } from 'react';
import ensureMapsLoaded from './loadMaps';
import { AmenityIconRow, MatchChip, PgPriceRow, StatusBadge } from './listingFeatures';
import { useAuth } from './AuthContext';
import { IconMapPin, IconUserPlus, IconFilter, IconSearch } from './components';
import { publicSearchApi, S3_BASE_URL, PRIME_PRICE_INR } from './api';
import { openPrimeModal } from './PrimeModal';
import PlaceSeoSection from './PlaceSeoSection';

// Inline Icons for Card UI
const IconMessage = ({ size = 24, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    </svg>
);

const IconMapPinSolid = ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
    </svg>
);

const IconUsers = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
);

const IconTool = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
    </svg>
);

const IconSend = ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13"></line>
        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
    </svg>
);

const IconEye = ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
        <circle cx="12" cy="12" r="3"></circle>
    </svg>
);


// Time ago helper
const getTimeAgo = (dateStr) => {
    if (!dateStr) return 'Recently';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    return `${weeks}w ago`;
};

// S3 Image component to fetch image directly from base URL
const S3Image = ({ s3Key, style, alt }) => {
    const [url, setUrl] = useState('');

    useEffect(() => {
        if (!s3Key) return;
        // Basic check if it's already an absolute URL
        if (s3Key.startsWith('http://') || s3Key.startsWith('https://') || s3Key.startsWith('data:')) {
            setUrl(s3Key);
        } else {
            // Prepend S3 base URL cleanly
            const cleanKey = s3Key.startsWith('/') ? s3Key.substring(1) : s3Key;
            setUrl(`${S3_BASE_URL}${cleanKey}`);
        }
    }, [s3Key]);

    if (!url) {
        return (
            <div style={{ ...style, background: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: '#9CA3AF', fontSize: '10px' }}>Loading...</span>
            </div>
        );
    }
    return <img src={url} alt={alt} style={style} />;
};

/**
 * @param place set when the page is a /flats-in-<place> landing page; adds the
 *              keyword heading and the written section under the results.
 */
const Dashboard = ({ onRequireLogin, initialLocation, onNavigate, place = null }) => {
    const { tokens, isSubscribed } = useAuth();

    // UI States
    const [activeTab, setActiveTab] = useState('listings'); // 'listings' | 'requirements'
    const [loading, setLoading] = useState(true);

    // Data States
    const [listings, setListings] = useState([]);
    const [listingTotal, setListingTotal] = useState(0); // server-side total; `listings` is one page
    const [requirements, setRequirements] = useState([]);


    // Filter and Detail States
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({ rentMin: '', rentMax: '', postType: 'ANY', gender: 'ANY', furnishing: 'ANY' });

    // Location Search States
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isSearchingLocation, setIsSearchingLocation] = useState(false);
    const [currentLocation, setCurrentLocation] = useState(initialLocation || { name: 'Delhi', lat: 28.6138, lng: 77.2090 });

    const searchRef = useRef(null);
    const autocompleteService = useRef(null);
    const placesService = useRef(null);

    useEffect(() => {
        if (initialLocation) {
            setCurrentLocation(initialLocation);
        }
    }, [initialLocation]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        ensureMapsLoaded().catch(() => {});
        const initGoogleMaps = () => {
            if (window.google && window.google.maps && window.google.maps.places) {
                autocompleteService.current = new window.google.maps.places.AutocompleteService();
                const dummyDiv = document.createElement('div');
                placesService.current = new window.google.maps.places.PlacesService(dummyDiv);
            }
        };

        if (window.google) {
            initGoogleMaps();
        } else {
            // If the script hasn't loaded yet, check periodically
            const interval = setInterval(() => {
                if (window.google) {
                    initGoogleMaps();
                    clearInterval(interval);
                }
            }, 100);
            return () => clearInterval(interval);
        }
    }, []);

    // Initial Bootstrap: Fetch User Info & Chats
    useEffect(() => {
        const fetchUserData = async () => {
            if (!tokens?.access) return;
            try {
                // Fetch live unread chat room count via context or app level if needed in future.
                // Profile natively fetched via AuthContext now.
            } catch (err) {
                console.error('Failed to load user info', err);
            }
        };
        fetchUserData();
    }, [tokens]);

    // Fetch primary feed data based on active tab, location, and filters
    const fetchFeed = async (lat, lng, tab, activeFilters = filters) => {
        setLoading(true);
        try {
            const params = { lat, lng, radiusKm: 20 };
            if (activeFilters.rentMin) params.rentMin = activeFilters.rentMin;
            if (activeFilters.rentMax) params.rentMax = activeFilters.rentMax;
            if (activeFilters.postType && activeFilters.postType !== 'ANY') params.postType = activeFilters.postType;
            if (activeFilters.gender && activeFilters.gender !== 'ANY') params.gender = activeFilters.gender;
            if (activeFilters.furnishing && activeFilters.furnishing !== 'ANY') params.furnishing = activeFilters.furnishing;

            if (tab === 'listings') {
                const res = await publicSearchApi.searchListings(params);
                setListings(res?.content || []);
                setListingTotal(res?.totalElements ?? (res?.content || []).length);
            } else {
                const res = await publicSearchApi.searchRequirements(params);
                setRequirements(res?.content || []);
            }
        } catch (err) {
            console.error(`Failed to load ${tab}`, err);
            // Gracefully handle errors (e.g. API unreachable) — don't crash
            if (tab === 'listings') setListings([]);
            else setRequirements([]);
        } finally {
            setLoading(false);
        }
    };

    // Refetch when Tab or Location drastically changes
    useEffect(() => {
        fetchFeed(currentLocation.lat, currentLocation.lng, activeTab, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, currentLocation.lat, currentLocation.lng]);

    const applyFilters = () => {
        setShowFilters(false);
        fetchFeed(currentLocation.lat, currentLocation.lng, activeTab, filters);
    };

    const clearFilters = () => {
        const defaultFilters = { rentMin: '', rentMax: '', postType: 'ANY', gender: 'ANY', furnishing: 'ANY' };
        setFilters(defaultFilters);
        setShowFilters(false);
        fetchFeed(currentLocation.lat, currentLocation.lng, activeTab, defaultFilters);
    };

    const openDetails = (type, id) => {
        window.open(`/details?type=${type}&id=${id}&lat=${currentLocation.lat}&lng=${currentLocation.lng}`, '_blank');
    };

    // Google Places Search using JS SDK
    const searchTimeout = useRef(null);
    const handleSearchInput = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        if (searchTimeout.current) clearTimeout(searchTimeout.current);

        if (!value.trim()) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        setShowSuggestions(true);
        setIsSearchingLocation(true);

        searchTimeout.current = setTimeout(() => {
            if (!autocompleteService.current) {
                console.warn('Google Maps API not loaded.');
                setIsSearchingLocation(false);
                return;
            }

            autocompleteService.current.getPlacePredictions(
                { input: value, componentRestrictions: { country: 'in' } }, // Limit search to India
                (predictions, status) => {
                    setIsSearchingLocation(false);
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                        setSuggestions(predictions);
                    } else {
                        setSuggestions([]);
                    }
                }
            );
        }, 500);
    };

    const handleSelectSuggestion = (suggestion) => {
        const placeId = suggestion.place_id;
        const mainText = suggestion.structured_formatting.main_text;
        const fullText = suggestion.description;

        setSearchQuery(fullText);
        setShowSuggestions(false);
        setLoading(true);

        if (!placesService.current) {
            console.error('PlacesService is not initialized.');
            setLoading(false);
            return;
        }

        placesService.current.getDetails(
            { placeId, fields: ['geometry'] },
            (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();
                    setCurrentLocation({ name: mainText, lat, lng });
                    // fetchFeed is correctly triggered by useEffect watching currentLocation
                } else {
                    console.error('Place Details Error:', status);
                    setLoading(false);
                }
            }
        );
    };

    const BLUE = '#2563EB';
    const BLUE_DARK = '#1E40AF';

    return (
        <div style={styles.dashboard}>

            {/* ===== BLUE GRADIENT HEADER ===== */}
            <header style={{
                background: `linear-gradient(155deg, ${BLUE_DARK} 0%, ${BLUE} 55%, #3B82F6 100%)`,
                padding: '100px 20px 56px',
                position: 'relative',
                overflowX: 'clip',
            }}>
                {/* Orbs */}
                <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '380px', height: '380px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '260px', height: '260px', background: 'rgba(255,255,255,0.04)', borderRadius: '50%', pointerEvents: 'none' }} />

                <div style={{ maxWidth: '860px', margin: '0 auto', position: 'relative', zIndex: 10 }}>
                    {/* Title */}
                    <div style={{ marginBottom: '6px', display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)', padding: '5px 16px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.2)' }}>
                        <span style={{ fontSize: '12px', color: 'white', fontWeight: '700', letterSpacing: '0.5px' }}>Zero Brokerage · Phone-Verified</span>
                    </div>
                    <h1 style={{ fontSize: 'clamp(26px, 5vw, 42px)', fontWeight: '800', color: 'white', marginBottom: '6px', letterSpacing: '-1px', lineHeight: '1.15' }}>
                        {place && currentLocation.name === place.name ? (
                            /* Matches what people type: "flats for rent in noida", "pg in noida". */
                            <>Flats, Rooms &amp; PG for Rent in <span style={{ color: '#93C5FD' }}>{place.name}</span></>
                        ) : (
                            <>Flats &amp; Rooms near <span style={{ color: '#93C5FD' }}>{currentLocation.name}</span></>
                        )}
                    </h1>
                    <p style={{ color: '#BFDBFE', fontSize: '15px', marginBottom: '28px', fontWeight: '400' }}>Direct chat with verified owners. No middlemen, no brokerage — unlock contacts with Prime at ₹{PRIME_PRICE_INR}.</p>

                    {/* Search bar — wrapper ref captures both input + dropdown */}
                    <div style={{ position: 'relative', maxWidth: '640px', zIndex: 300 }} ref={searchRef}>
                        <div style={{
                            background: 'rgba(255,255,255,0.14)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '18px',
                            padding: '6px',
                            border: '1px solid rgba(255,255,255,0.28)',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', background: 'white', borderRadius: '13px', padding: '4px 4px 4px 18px' }}>
                                <div style={{ color: BLUE, display: 'flex', marginRight: '10px', flexShrink: 0 }}><IconSearch /></div>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={handleSearchInput}
                                    onFocus={() => { if (searchQuery) setShowSuggestions(true); }}
                                    placeholder="Search locality, landmark, or city..."
                                    aria-label="Search location"
                                    style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: '15px', fontWeight: '500', color: '#1F2937', padding: '13px 0' }}
                                />
                                <button
                                    onClick={() => { }}
                                    style={{ background: `linear-gradient(135deg, ${BLUE_DARK}, #3B82F6)`, color: 'white', border: 'none', padding: '12px 24px', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', fontSize: '14px', flexShrink: 0, boxShadow: '0 4px 14px rgba(37,99,235,0.4)', whiteSpace: 'nowrap' }}
                                >
                                    Search
                                </button>
                            </div>
                        </div>

                        {/* Suggestions dropdown — rendered inside zIndex:300 wrapper, escapes orb overflow */}
                        {showSuggestions && (
                            <div style={{
                                position: 'absolute', top: 'calc(100% + 12px)', left: 0, right: 0,
                                background: 'white', borderRadius: '20px',
                                boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
                                zIndex: 9999, border: '1px solid #E5E7EB', padding: '8px',
                                overflow: 'hidden'
                            }}>
                                {isSearchingLocation ? (
                                    <div style={{ padding: '18px', color: '#6B7280', textAlign: 'center', fontSize: '14px' }}>Searching places...</div>
                                ) : suggestions.length > 0 ? (
                                    suggestions.map((sugg, idx) => (
                                        <div key={idx} onClick={() => handleSelectSuggestion(sugg)}
                                            style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', cursor: 'pointer', borderRadius: '12px', transition: 'background 0.15s' }}
                                            onMouseOver={e => e.currentTarget.style.background = '#EFF6FF'}
                                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                                            <div style={{ width: '36px', height: '36px', background: '#EFF6FF', color: BLUE, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><IconMapPin /></div>
                                            <div style={{ marginLeft: '12px' }}>
                                                <div style={{ fontWeight: '700', color: '#1F2937', fontSize: '14px' }}>{sugg.structured_formatting?.main_text || sugg.description}</div>
                                                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{sugg.structured_formatting?.secondary_text || ''}</div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ padding: '18px', color: '#6B7280', textAlign: 'center', fontSize: '14px' }}>No places found.</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </header>
            {/* ===== END BLUE HEADER ===== */}

            <div className="container">

                {/* TAB SELECTOR */}
                <div style={styles.tabsContainer}>
                    <button onClick={() => setActiveTab('listings')} style={activeTab === 'listings' ? styles.tabActive : styles.tabInactive}>🏠 Rooms</button>
                    <button onClick={() => setActiveTab('requirements')} style={activeTab === 'requirements' ? styles.tabActive : styles.tabInactive}>👥 Roommates</button>
                    <button
                        onClick={() => setShowFilters(true)}
                        style={{ background: 'linear-gradient(135deg,#1E40AF,#2563EB)', color: 'white', border: 'none', marginLeft: 'auto', padding: '9px 18px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }}
                    >
                        <IconFilter /> Filters
                    </button>
                </div>

                {/* Rent Insights Banner */}
                <div
                    onClick={() => onNavigate && onNavigate('rentInsights')}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '14px',
                        background: 'linear-gradient(135deg, #ECFDF5, #D1FAE5)',
                        border: '1px solid #A7F3D0', borderRadius: '16px',
                        padding: '14px 20px', marginTop: '12px', cursor: 'pointer',
                        transition: 'all 0.2s',
                    }}
                    onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(5,150,105,0.15)'; }}
                    onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                    <div style={{ width: '40px', height: '40px', background: '#059669', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>📊</div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '800', fontSize: '14px', color: '#065F46' }}>
                            What's the rent in {currentLocation.name || 'your area'}?
                        </div>
                        <div style={{ fontSize: '12px', color: '#059669', fontWeight: '600', marginTop: '2px' }}>
                            Check real prices from community data →
                        </div>
                    </div>
                    <div style={{ background: '#059669', color: 'white', padding: '6px 14px', borderRadius: '10px', fontSize: '12px', fontWeight: '800', flexShrink: 0 }}>NEW</div>
                </div>

                {/* FEED CONTENT */}
                <div style={{ marginTop: '16px' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '80px 0', opacity: 0.5, fontSize: '18px' }}>
                            <div className="icon-wrap" style={{ margin: '0 auto 16px', color: '#10B981', animation: 'pulseGlow 2s infinite' }}><IconMapPinSolid size={32} /></div>
                            Scanning location...
                        </div>
                    ) : activeTab === 'listings' ? (
                        /* LISTINGS RENDERER — Mobile App Style */
                        <div className="feed-grid">
                            {listings.length === 0 ? (
                                <div style={styles.emptyCard}>
                                    <div className="icon-wrap" style={{ margin: '0 auto 16px', background: '#FEF2F2', color: '#DC2626' }}><IconMapPin /></div>
                                    <h3>No properties found here.</h3>
                                    <p style={{ color: 'var(--text-muted)' }}>Try broadening your search radius or changing location.</p>
                                </div>
                            ) : (
                                listings.map((item) => (
                                    <div key={item.id} style={{ cursor: 'pointer', background: 'white', borderRadius: '24px', border: '1px solid #F3F4F6', boxShadow: '0 4px 16px rgba(0,0,0,0.05)', overflow: 'hidden', transition: 'transform 0.2s, box-shadow 0.2s' }}
                                        onClick={() => openDetails('listing', item.id)}
                                        onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.1)'; }}
                                        onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.05)'; }}>

                                        {/* === IMAGE SECTION === */}
                                        <div style={{ position: 'relative', width: '100%', height: '200px', background: '#E5E7EB', overflow: 'hidden' }}>
                                            {item.thumbnailImageUrl ? (
                                                <img src={`${S3_BASE_URL}${item.thumbnailImageUrl}`} alt={item.location?.name || 'Property'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #E0E7FF 0%, #DBEAFE 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#93C5FD', fontSize: '48px' }}>🏠</div>
                                            )}
                                            {/* Post type badge */}
                                            {item.postType && (
                                                <div style={{
                                                    position: 'absolute', top: '14px', left: '14px',
                                                    background: item.postType === 'PG' ? 'linear-gradient(135deg, #F59E0B, #D97706)' : item.postType === 'ROOM' ? 'linear-gradient(135deg, #10B981, #047857)' : 'linear-gradient(135deg, #3B82F6, #2563EB)',
                                                    color: 'white', padding: '5px 14px', borderRadius: '12px', fontSize: '12px', fontWeight: '800',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                }}>
                                                    {item.postType}
                                                </div>
                                            )}
                                            {/* Status — only renders when the listing is not ACTIVE. */}
                                            {item.status && item.status !== 'ACTIVE' && (
                                                <div style={{ position: 'absolute', top: '14px', right: '14px' }}>
                                                    <StatusBadge status={item.status} />
                                                </div>
                                            )}
                                            {/* Price badge */}
                                            <div style={{
                                                position: 'absolute', bottom: '14px', right: '14px',
                                                background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)',
                                                color: 'white', padding: '6px 14px', borderRadius: '14px',
                                                fontSize: '16px', fontWeight: '800',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                            }}>
                                                ₹{item.rent?.toLocaleString()}<span style={{ fontSize: '12px', fontWeight: '500', opacity: 0.8 }}>/mo</span>
                                            </div>
                                        </div>

                                        {/* === INFO SECTION === */}
                                        <div style={{ padding: '16px' }}>
                                            {/* User info + looking for badge */}
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    {item.profile?.profilePic ? (
                                                        <S3Image s3Key={item.profile.profilePic} alt="Owner" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                                                    ) : (
                                                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#E0E7FF', color: '#4F46E5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '14px', flexShrink: 0 }}>
                                                            {(item.profile?.name || 'U').charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                    <span style={{ fontSize: '15px', fontWeight: '700', color: '#1F2937' }}>{item.profile?.name || 'User'}</span>
                                                </div>
                                                <span style={{ background: '#E0E7FF', color: '#4F46E5', padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}>
                                                    <IconUsers size={12} /> Looking for {item.lookingGender === 'ANY' ? 'Any' : item.lookingGender === 'FEMALE' ? 'Female' : item.lookingGender === 'MALE' ? 'Male' : item.lookingGender}
                                                </span>
                                            </div>

                                            {/* Location */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#6B7280', fontSize: '13px', marginBottom: '10px' }}>
                                                <IconMapPinSolid size={14} color="#10B981" />
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{item.location?.name || 'Location Protected'}</span>
                                                <MatchChip score={item.lifestyleMatchScore} />
                                            </div>

                                            {/* Tags row */}
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                                                {item.postType && (
                                                    <span style={{ background: item.postType === 'PG' ? '#FEF3C7' : '#E0E7FF', color: item.postType === 'PG' ? '#D97706' : '#4F46E5', padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '700' }}>
                                                        {item.postType}
                                                    </span>
                                                )}
                                                {item.bhk > 0 && (
                                                    <span style={{ background: '#F3F4F6', color: '#4B5563', padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '600' }}>
                                                        {item.bhk} BHK
                                                    </span>
                                                )}
                                                {item.furnishing && item.furnishing !== 'ANY' && (
                                                    <span style={{ background: '#F3F4F6', color: '#4B5563', padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <IconTool size={12} /> {item.furnishing === 'SEMI' ? 'Semi-Furnished' : item.furnishing === 'FURNISHED' ? 'Furnished' : 'Unfurnished'}
                                                    </span>
                                                )}
                                                {item.negotiable && (
                                                    <span style={{ background: '#D1FAE5', color: '#10B981', padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '700' }}>
                                                        Negotiable
                                                    </span>
                                                )}
                                            </div>

                                            {item.postType === 'PG' && (
                                                <PgPriceRow payingGuest={item.payingGuest} style={{ marginBottom: '12px' }} />
                                            )}

                                            {/* Amenities only. The app's ListingCard destructures
                                                nearbyPlaces but never renders it — those belong on the
                                                detail page, and stacked here they were a second row of
                                                identical grey circles that no reader could tell apart
                                                from the amenities above them. */}
                                            <AmenityIconRow amenities={item.amenties} style={{ marginBottom: '12px' }} />

                                            {/* Bottom row: distance, views, time, chat */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '10px', borderTop: '1px solid #F3F4F6' }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#2563EB', fontSize: '13px', fontWeight: '700' }}>
                                                    <IconSend size={13} /> {item.distance ? item.distance.toFixed(1) : '0.0'} km
                                                </span>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#9CA3AF', fontSize: '13px' }}>
                                                    <IconEye size={13} /> {item.viewCount || 1}
                                                </span>
                                                <span style={{ color: '#9CA3AF', fontSize: '13px' }}>
                                                    {getTimeAgo(item.createdAt)}
                                                </span>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (!tokens?.access) {
                                                            onRequireLogin();
                                                        } else if (!isSubscribed) {
                                                            // Server would return 402 anyway; ask up front
                                                            // rather than opening a chat that can't send.
                                                            openPrimeModal('Chat with owners and flatmates without limits.');
                                                        } else {
                                                            window.dispatchEvent(new CustomEvent('openChat', {
                                                                detail: {
                                                                    listingId: String(item.id),
                                                                    receiverUserId: item.ownerId || item.profile?.userId || '',
                                                                    receiverName: item.profile?.name || item.ownerName || '',
                                                                    receiverPic: item.profile?.profilePic || item.ownerProfilePic || '',
                                                                }
                                                            }));
                                                        }
                                                    }}
                                                    style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#2563EB', color: 'white', border: 'none', marginLeft: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(37,99,235,0.3)', transition: 'all 0.15s', flexShrink: 0 }}
                                                    onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
                                                    onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                                                >
                                                    <IconMessage size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    ) : (
                        /* REQUIREMENTS RENDERER */
                        <div className="feed-grid">
                            {requirements.length === 0 ? (
                                <div style={styles.emptyCard}>
                                    <div className="icon-wrap" style={{ margin: '0 auto 16px', background: '#F0FDF4', color: '#16A34A' }}><IconUserPlus /></div>
                                    <h3>No active requests found here.</h3>
                                    <p style={{ color: 'var(--text-muted)' }}>Nobody is currently looking for a place in this specific area.</p>
                                </div>
                            ) : (
                                requirements.map((req) => (
                                    <div key={req.id} style={{ padding: '16px', display: 'flex', gap: '16px', cursor: 'pointer', background: 'white', borderRadius: '24px', border: '1px solid #F3F4F6', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }} onClick={() => openDetails('requirement', req.id)}>
                                        {req.ownerProfilePic || req.profile?.profilePic ? (
                                            <img src={`${S3_BASE_URL}${req.ownerProfilePic || req.profile?.profilePic}`} alt="Searcher" style={{ width: '80px', height: '80px', borderRadius: '16px', objectFit: 'cover', flexShrink: 0 }} />
                                        ) : (
                                            <div style={{ width: '80px', height: '80px', borderRadius: '16px', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><IconUserPlus size={40} color="#9ca3af" /></div>
                                        )}

                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <div style={{ fontSize: '16px', fontWeight: '800', color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {req.ownerName || req.profile?.name || 'User'}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6B7280', fontSize: '13px' }}>
                                                    <IconMapPinSolid size={14} color="#10B981" />
                                                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{req.location?.name || 'Any Location Nearby'}</span>
                                                </div>
                                                <div style={{ fontSize: '16px', fontWeight: '800', color: '#10B981', marginTop: '4px' }}>
                                                    ₹{req.budgetMin?.toLocaleString() || 0} - ₹{req.budgetMax?.toLocaleString() || 0}
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                                                <span style={{ background: '#E0E7FF', color: '#4F46E5', padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '700' }}>
                                                    {req.postType === 'ANY' ? 'Flat/PG' : (req.postType === 'ROOM' ? 'Room' : (req.postType === 'FLAT' ? 'Flat' : req.postType))}
                                                </span>
                                                <span style={{ background: '#F3F4F6', color: '#4B5563', padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <IconUsers size={12} />
                                                    Looking for {req.lookingGender ? (req.lookingGender === 'ANY' ? 'Any' : (req.lookingGender === 'FEMALE' ? 'Female' : 'Male')) : 'Any'}
                                                </span>
                                                {req.furnishing && req.furnishing !== 'ANY' && (
                                                    <span style={{ background: '#F3F4F6', color: '#4B5563', padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <IconTool size={12} /> {req.furnishing === 'SEMI' ? 'Semi-Furnished' : (req.furnishing === 'FURNISHED' ? 'Furnished' : 'Unfurnished')}
                                                    </span>
                                                )}
                                                <span style={{ background: '#F3F4F6', color: '#4B5563', padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <IconUserPlus size={12} />
                                                    {req.occupany && req.occupany !== 'ANY' ? req.occupany.charAt(0) + req.occupany.slice(1).toLowerCase() : 'Single'}
                                                </span>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px' }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#10B981', fontSize: '12px', fontWeight: '700' }}>
                                                    <IconSend size={12} /> {req.distance ? req.distance.toFixed(1) : '0.0'} km
                                                </span>
                                                <span style={{ color: '#9CA3AF', fontSize: '12px' }}>
                                                    Today
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                {place && currentLocation.name === place.name && activeTab === 'listings' && (
                    <PlaceSeoSection place={place} listings={listings} total={listingTotal} loading={loading} />
                )}

            </div> {/* container */}

            {/* FILTER MODAL */}
            {showFilters && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalCard}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>Filters</h2>
                            <button onClick={() => setShowFilters(false)} style={styles.closeModalBtn}>&times;</button>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={styles.label}>Rent / Budget Range (₹)</label>
                            <div style={{ display: 'flex', gap: '16px' }}>
                                <input type="number" placeholder="Min" value={filters.rentMin} onChange={e => setFilters({ ...filters, rentMin: e.target.value })} style={styles.inputField} />
                                <input type="number" placeholder="Max" value={filters.rentMax} onChange={e => setFilters({ ...filters, rentMax: e.target.value })} style={styles.inputField} />
                            </div>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={styles.label}>Property Type</label>
                            <select value={filters.postType} onChange={e => setFilters({ ...filters, postType: e.target.value })} style={styles.inputField}>
                                <option value="ANY">Any</option>
                                <option value="FLAT">Flat</option>
                                <option value="ROOM">Room</option>
                                <option value="PG">PG</option>
                            </select>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={styles.label}>Preferred Gender</label>
                            <select value={filters.gender} onChange={e => setFilters({ ...filters, gender: e.target.value })} style={styles.inputField}>
                                <option value="ANY">Any</option>
                                <option value="MALE">Male Only</option>
                                <option value="FEMALE">Female Only</option>
                            </select>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={styles.label}>Furnishing Status</label>
                            <select value={filters.furnishing} onChange={e => setFilters({ ...filters, furnishing: e.target.value })} style={styles.inputField}>
                                <option value="ANY">Any</option>
                                <option value="FURNISHED">Fully Furnished</option>
                                <option value="SEMI">Semi Furnished</option>
                                <option value="UNFURNISHED">Unfurnished</option>
                            </select>
                        </div>

                        <div style={{ display: 'flex', gap: '16px', marginTop: '32px' }}>
                            <button onClick={applyFilters} style={{ ...styles.actionBtn, background: '#10B981', color: 'white' }}>Apply Filters</button>
                            <button onClick={clearFilters} style={{ ...styles.actionBtn, background: '#F1F5F9', color: '#475569' }}>Clear</button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

const styles = {
    dashboard: {
        paddingBottom: '80px', minHeight: '80vh',
        background: '#F8FAFF'
    },
    tabsContainer: {
        display: 'flex', alignItems: 'center',
        borderBottom: '1px solid #E5E7EB',
        marginTop: '20px', marginBottom: '8px',
        background: 'white',
        padding: '4px 4px 0'
    },
    tabActive: {
        flex: 1, color: '#2563EB', padding: '14px 0', textAlign: 'center',
        background: 'transparent', fontWeight: '800', border: 'none',
        borderBottom: '3px solid #2563EB', cursor: 'pointer', transition: '0.2s', fontSize: '14px'
    },
    tabInactive: {
        flex: 1, color: '#6B7280', padding: '14px 0', textAlign: 'center',
        background: 'transparent', fontWeight: '700', border: 'none',
        borderBottom: '3px solid transparent', cursor: 'pointer', transition: '0.2s', fontSize: '14px'
    },
    emptyCard: {
        gridColumn: '1 / -1', textAlign: 'center', padding: '80px 40px',
        background: 'white', borderRadius: '32px', boxShadow: '0 10px 40px rgba(0,0,0,0.03)',
        border: '1px solid #E5E7EB'
    },
    modalOverlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(8px)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
    },
    modalCard: {
        background: 'white', width: '100%', maxWidth: '440px',
        borderRadius: '24px', padding: '32px',
        boxShadow: '0 24px 48px rgba(0,0,0,0.12)', animation: 'fadeUp 0.3s ease'
    },
    closeModalBtn: {
        background: '#F1F5F9', border: 'none', width: '36px', height: '36px',
        borderRadius: '18px', fontSize: '20px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569'
    },
    label: {
        display: 'block', fontSize: '14px', fontWeight: '700', color: '#334155', marginBottom: '8px'
    },
    inputField: {
        width: '100%', padding: '12px 16px', borderRadius: '12px',
        border: '1px solid #CBD5E1', outline: 'none', background: '#F8FAFC', fontSize: '15px'
    },
    actionBtn: {
        flex: 1, padding: '14px', borderRadius: '12px',
        fontWeight: 'bold', border: 'none', cursor: 'pointer', fontSize: '16px'
    },
};

export default Dashboard;
